import { can } from '../utils/permissions';
import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import admissionService from '../services/admissionService';
import roomService from '../services/roomService ';        // ← added

/* ─── helpers ───────────────────────────────── */
const Avatar = ({ name, gender, studentId, size = 38 }) => {
  const initials = (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  const bg = gender==='Female' ? '#E8D5E8' : '#D5E3F0';
  const color = gender==='Female' ? '#7B4F8B' : '#1B3B5F';
  const [imgOk, setImgOk] = React.useState(!!studentId);
  return (
    <div style={{ width:size,height:size,borderRadius:'50%',flexShrink:0,background:bg,color,fontWeight:700,fontSize:size*0.32,display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden',position:'relative' }}>
      {studentId && imgOk ? (
        <img
          src={`https://web.omkarahostel.com/OmkaraHostel/students/${studentId}/photo`}
          alt={name}
          style={{ width:'100%',height:'100%',objectFit:'cover' }}
          onError={() => setImgOk(false)}
        />
      ) : initials}
    </div>
  );
};

const PABadge = ({ presence }) => (
  <div style={{
    width:30,height:30,borderRadius:'50%',display:'inline-flex',alignItems:'center',justifyContent:'center',
    fontWeight:700,fontSize:12,
    background: presence==='IN' ? '#E8F5E9' : '#FFEBEE',
    color: presence==='IN' ? '#2D5F4F' : '#DC3545',
  }}>
    {presence==='IN'?'P':'A'}
  </div>
);

const InOutBadge = ({ presence, onClick }) => (
  <span onClick={onClick} title="Click to toggle" style={{
    padding:'4px 12px',borderRadius:12,fontSize:11,fontWeight:700,letterSpacing:0.5,
    cursor:'pointer',userSelect:'none',
    background: presence==='IN' ? '#E8F5E9' : '#FFEBEE',
    color: presence==='IN' ? '#2D5F4F' : '#DC3545',
  }}>{presence}</span>
);

/* ─────────────────────────────────────────────────────────
   ALLOCATE ROOM MODAL  (3-step real bed picker)
   ─────────────────────────────────────────────────────────
   Step 1 → Select Tower (A/B) + Floor (1-7)   [was: Tower A/B/C + Floor 0-4 only]
   Step 2 → Real rooms/beds from roomService.getFloor()
            Grey=vacant(clickable)  Red=occupied(blocked)  Green=selected
   Step 3 → Summary → confirm
   On confirm → roomService.allocateBed() which does:
     POST  /rooms/beds/allocate       marks bed OCCUPIED in beds table
     PATCH /admissions/{id}/room      saves bedCode into admission record
──────────────────────────────────────────────────────────── */
const AllocateModal = ({ student, onClose, onSave }) => {
  const [step,        setStep]         = useState(1);
  const [tower,       setTower]        = useState('A');
  const [floor,       setFloor]        = useState(2);
  const [floorData,   setFloorData]    = useState(null);
  const [loadingFloor,setLoadingFloor] = useState(false);
  const [floorError,  setFloorError]   = useState('');
  const [selectedBed, setSelectedBed]  = useState(null);
  const [saving,      setSaving]       = useState(false);

  // Load real room/bed data when entering step 2
  const loadFloor = useCallback(async () => {
    setLoadingFloor(true); setFloorError(''); setFloorData(null); setSelectedBed(null);
    try {
      const data = await roomService.getFloor(tower, floor);
      setFloorData(data);
    } catch (e) {
      setFloorError('Could not load floor data — is the backend running?');
    } finally {
      setLoadingFloor(false);
    }
  }, [tower, floor]);

  useEffect(() => { if (step === 2) loadFloor(); }, [step, loadFloor]);

  // "AR-211" → { roomBlock:"AR", roomNumber:"21", bedNumber:"1" }
  const parseBed = (bedCode) => {
    const dash = bedCode.indexOf('-');
    const rest = bedCode.substring(dash + 1);
    return {
      roomBlock:  bedCode.substring(0, dash),
      roomNumber: rest.substring(0, rest.length - 1),
      bedNumber:  rest.substring(rest.length - 1),
    };
  };

  // "AR-21" → "Tower A · Rear · Floor 2 · Room 1"
  const roomLabel = (roomCode) => {
    const p = roomCode.substring(0, 2);
    const r = roomCode.substring(3);
    return `Tower ${p[0]} · ${p[1]==='R'?'Rear':'Front'} · Floor ${r.slice(0,-1)} · Room ${r.slice(-1)}`;
  };

  const handleConfirm = async () => {
    if (!selectedBed) return;
    // Guard: student must not already have a bed
    if (student.bedCode || student.roomBlock) {
      alert(
        `${student.fullName} already has bed ${student.bedCode || student.roomBlock + '-' + student.roomNumber} allocated.
` +
        `Please vacate the current bed before allocating a new one.`
      );
      onClose();
      return;
    }
    setSaving(true);
    try {
      // roomService.allocateBed handles BOTH api calls:
      // 1) marks bed OCCUPIED   2) saves bedCode to admission record
      await roomService.allocateBed(
        student.id,
        selectedBed.bedCode,
        student.fullName,
        student.admissionNumber || student.registrationId,
      );
      onSave();   // refreshes the admissions table
      onClose();
    } catch (e) {
      alert(e.message || 'Failed to allocate bed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(27,59,95,0.65)',zIndex:1200,
      display:'flex',alignItems:'center',justifyContent:'center'}} onClick={onClose}>
      <div style={{background:'#fff',width:680,maxHeight:'90vh',borderRadius:8,overflow:'hidden',
        boxShadow:'0 24px 64px rgba(0,0,0,0.35)',display:'flex',flexDirection:'column'}}
        onClick={e=>e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={{background:'#1B3B5F',padding:'22px 36px',flexShrink:0}}>
          <h3 style={{color:'#fff',margin:0,fontFamily:'Cormorant Garamond,serif',fontSize:24,fontWeight:400}}>
            Allocate Room
          </h3>
          <div style={{display:'flex', alignItems:'center', gap:10, margin:'4px 0 0', flexWrap:'wrap'}}>
            <p style={{color:'rgba(255,255,255,0.85)', margin:0, fontSize:13, fontWeight:600}}>{student?.fullName}</p>
            {student?.roomType && (
              <span style={{
                fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20,
                background:'rgba(212,175,55,0.25)', color:'#D4AF37',
                border:'1px solid rgba(212,175,55,0.4)', letterSpacing:0.5, textTransform:'uppercase'
              }}>
                {{'4sharing':'4 Sharing','3sharing':'3 Sharing','2sharing':'2 Sharing (Twin)',
                  '2sharingPremium':'2 Sharing (Premium)','private':'Single / Private'}[student.roomType] || student.roomType}
              </span>
            )}
          </div>
          {/* Step indicator */}
          <div style={{display:'flex',alignItems:'center',gap:6,marginTop:14}}>
            {['Select Floor','Pick Bed','Confirm'].map((lbl,i)=>(
              <React.Fragment key={i}>
                <div style={{display:'flex',alignItems:'center',gap:5}}>
                  <div style={{width:20,height:20,borderRadius:'50%',display:'flex',alignItems:'center',
                    justifyContent:'center',fontSize:10,fontWeight:700,
                    background:step>i+1?'#4CAF50':step===i+1?'#D4AF37':'rgba(255,255,255,0.2)',
                    color:'white'}}>
                    {step>i+1?'✓':i+1}
                  </div>
                  <span style={{fontSize:11,fontWeight:step===i+1?700:400,
                    color:step===i+1?'#D4AF37':'rgba(255,255,255,0.5)'}}>
                    {lbl}
                  </span>
                </div>
                {i<2&&<span style={{color:'rgba(255,255,255,0.25)',fontSize:13}}>›</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{padding:28,overflowY:'auto',flex:1}}>

          {/* STEP 1 — Select Tower + Floor (1-7) */}
          {step===1&&(
            <div>
              {/* Tower picker */}
              <div style={{marginBottom:24}}>
                <label style={{display:'block',fontSize:11,fontWeight:700,letterSpacing:1,
                  textTransform:'uppercase',color:'#6B6B6B',marginBottom:10}}>Select Tower</label>
                <div style={{display:'flex',gap:12}}>
                  {['A','B'].map(t=>(
                    <button key={t} onClick={()=>setTower(t)} style={{
                      flex:1,padding:16,borderRadius:6,cursor:'pointer',fontWeight:700,fontSize:15,
                      border:`2px solid ${tower===t?'#1B3B5F':'#E8E4DF'}`,
                      background:tower===t?'#1B3B5F':'white',
                      color:tower===t?'white':'#2C2C2C',transition:'all 0.2s'}}>
                      🏢 Tower {t}
                      <div style={{fontSize:11,fontWeight:400,marginTop:4,opacity:0.75}}>
                        {t==='A'?'Convenience Store':'Dining Hall'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Floor picker — ALL 7 floors */}
              <div>
                <label style={{display:'block',fontSize:11,fontWeight:700,letterSpacing:1,
                  textTransform:'uppercase',color:'#6B6B6B',marginBottom:10}}>
                  Select Floor (1–7)
                </label>
                <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:8}}>
                  {[1,2,3,4,5,6,7].map(f=>(
                    <button key={f} onClick={()=>setFloor(f)} style={{
                      padding:'14px 6px',borderRadius:6,cursor:'pointer',fontWeight:700,
                      fontSize:18,transition:'all 0.2s',
                      border:`2px solid ${floor===f?'#D4AF37':'#E8E4DF'}`,
                      background:floor===f?'#D4AF37':'white',
                      color:floor===f?'white':'#2C2C2C'}}>
                      {f}
                      <div style={{fontSize:8,fontWeight:400,marginTop:2,opacity:0.7}}>Floor</div>
                      {f===1&&(
                        <div style={{fontSize:7,fontWeight:600,marginTop:1,
                          color:floor===1?'rgba(255,255,255,0.85)':'#F59E0B'}}>common</div>
                      )}
                    </button>
                  ))}
                </div>
                {floor===1&&(
                  <div style={{marginTop:10,padding:'10px 14px',background:'#FFF8E8',
                    border:'1px solid #F59E0B',borderRadius:6,fontSize:12,color:'#92400E'}}>
                    ⚠️ Floor 1 is the common area ({tower==='A'?'Convenience Store':'Dining Hall'}) — only rear rooms available
                  </div>
                )}
              </div>

              <div style={{marginTop:20,padding:'12px 16px',background:'#F8F6F3',borderRadius:6,fontSize:13,color:'#6B6B6B'}}>
                Selected: <strong style={{color:'#1B3B5F'}}>Tower {tower} — Floor {floor}</strong>
              </div>
            </div>
          )}

          {/* STEP 2 — Pick a bed from real data */}
          {step===2&&(
            <div>
              <div style={{marginBottom:14,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{fontSize:13,color:'#6B6B6B'}}>
                  Tower {tower} · Floor {floor} — click a&nbsp;
                  <span style={{color:'#4CAF50',fontWeight:700}}>green vacant</span> bed
                </div>
                <div style={{display:'flex',gap:10,fontSize:11}}>
                  {[{bg:'#D3D3D3',l:'Vacant'},{bg:'#DC3545',l:'Occupied'},{bg:'#4CAF50',l:'Selected'}].map(x=>(
                    <div key={x.l} style={{display:'flex',alignItems:'center',gap:4}}>
                      <div style={{width:11,height:11,borderRadius:2,background:x.bg}}/>
                      <span style={{color:'#888'}}>{x.l}</span>
                    </div>
                  ))}
                </div>
              </div>

              {loadingFloor&&<div style={{padding:40,textAlign:'center',color:'#888'}}>Loading rooms…</div>}
              {floorError&&(
                <div style={{padding:14,background:'#FFF3F3',border:'1px solid #E85D4A',
                  borderRadius:6,color:'#E85D4A',fontSize:13}}>{floorError}</div>
              )}

              {!loadingFloor&&floorData&&(
                <div>
                  {floorData.frontRooms?.length>0&&(
                    <div style={{marginBottom:20}}>
                      <div style={{fontSize:10,fontWeight:700,letterSpacing:2,textTransform:'uppercase',
                        color:'#9B9B9B',marginBottom:10,paddingBottom:6,borderBottom:'1px solid #F0EDE8'}}>
                        Front Wing
                      </div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
                        {floorData.frontRooms.map(room=>(
                          <BedPicker key={room.roomCode} room={room}
                            selectedBed={selectedBed} onSelect={setSelectedBed}
                            studentRoomType={student?.roomType}/>
                        ))}
                      </div>
                    </div>
                  )}
                  {floorData.rearRooms?.length>0&&(
                    <div>
                      <div style={{fontSize:10,fontWeight:700,letterSpacing:2,textTransform:'uppercase',
                        color:'#9B9B9B',marginBottom:10,paddingBottom:6,borderBottom:'1px solid #F0EDE8'}}>
                        Rear Wing
                      </div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
                        {floorData.rearRooms.map(room=>(
                          <BedPicker key={room.roomCode} room={room}
                            selectedBed={selectedBed} onSelect={setSelectedBed}
                            studentRoomType={student?.roomType}/>
                        ))}
                      </div>
                    </div>
                  )}
                  {!floorData.frontRooms?.length&&!floorData.rearRooms?.length&&(
                    <div style={{padding:30,textAlign:'center',color:'#888'}}>No rooms on this floor</div>
                  )}
                </div>
              )}

              {selectedBed&&(
                <div style={{marginTop:16,padding:'12px 16px',background:'#F0FFF4',
                  border:'2px solid #4CAF50',borderRadius:6,fontSize:13,fontWeight:600,color:'#2D5F4F'}}>
                  ✅ Selected: <strong>{selectedBed.bedCode}</strong>
                  <span style={{fontWeight:400,color:'#6B6B6B',marginLeft:8,fontSize:12}}>
                    ({roomLabel(selectedBed.bedCode.slice(0,-1))})
                  </span>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — Confirm summary */}
          {step===3&&selectedBed&&(()=>{
            const {roomBlock,roomNumber,bedNumber}=parseBed(selectedBed.bedCode);
            return (
              <div>
                <div style={{fontSize:16,fontWeight:600,color:'#1B3B5F',marginBottom:20}}>
                  Confirm Room Allocation
                </div>
                {[
                  {l:'Student',   v:`${student.fullName} (${student.registrationId||student.admissionNumber})`},
                  {l:'Stream',    v: student.stream || (student.courses?.[0]?.stream) || '—'},
                  {l:'Bed Code',  v:selectedBed.bedCode},
                  {l:'Location',  v:roomLabel(selectedBed.bedCode.slice(0,-1))},
                  {l:'Room Block',v:roomBlock},
                  {l:'Room No.',  v:roomNumber},
                  {l:'Bed No.',   v:bedNumber},
                  {l:'Status',    v:'VACANT → OCCUPIED'},
                ].map(row=>(
                  <div key={row.l} style={{display:'flex',padding:'11px 0',borderBottom:'1px solid #F0EDE8',gap:12}}>
                    <div style={{width:130,fontSize:10,fontWeight:700,letterSpacing:1,
                      textTransform:'uppercase',color:'#6B6B6B',paddingTop:2}}>{row.l}</div>
                    <div style={{fontSize:14,color:'#2C2C2C',fontWeight:500}}>{row.v}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* ── Footer buttons ── */}
        <div style={{padding:'16px 28px',borderTop:'1px solid #E8E4DF',
          display:'flex',justifyContent:'space-between',flexShrink:0}}>
          <button onClick={onClose} style={{padding:'10px 24px',border:'2px solid #E8E4DF',
            background:'transparent',borderRadius:4,cursor:'pointer',fontSize:13,fontWeight:600,color:'#6B6B6B'}}>
            Cancel
          </button>
          <div style={{display:'flex',gap:10}}>
            {step>1&&(
              <button onClick={()=>setStep(s=>s-1)} style={{padding:'10px 24px',
                border:'2px solid #1B3B5F',background:'white',borderRadius:4,
                cursor:'pointer',fontSize:13,fontWeight:600,color:'#1B3B5F'}}>
                ← Back
              </button>
            )}
            {step<3&&(
              <button
                onClick={()=>{
                  if(step===2&&!selectedBed){alert('Please click a vacant bed first');return;}
                  setStep(s=>s+1);
                }}
                style={{padding:'10px 28px',border:'none',borderRadius:4,
                  fontSize:13,fontWeight:600,color:'white',
                  background:step===2&&!selectedBed?'#ccc':'#1B3B5F',
                  cursor:step===2&&!selectedBed?'not-allowed':'pointer'}}>
                {step===1?'View Beds →':'Next →'}
              </button>
            )}
            {step===3&&(()=>{
              const studentType = student?.roomType;
              const bedType     = selectedBed?.roomType;
              const mismatch    = studentType && bedType && studentType !== bedType;
              const isDisabled  = saving || mismatch;
              return (
                <button onClick={!isDisabled ? handleConfirm : undefined}
                  disabled={isDisabled}
                  style={{
                    padding:'10px 28px', border:'none', borderRadius:4,
                    fontSize:13, fontWeight:700,
                    color: 'white',
                    background: saving ? '#aaa' : '#4CAF50',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  }}
                  title={mismatch ? 'Cannot allocate — room type does not match student selection' : ''}>
                  {saving ? 'Allocating…' : mismatch ? '⛔ Type Mismatch' : '✅ Confirm Allocation'}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── BedPicker: single room card with clickable beds ── */
const BedPicker = ({ room, selectedBed, onSelect, studentRoomType }) => {
  const total = room.capacity || 0;
  const occ   = room.occupiedBeds || 0;

  // Check if this room type matches what student selected at admission
  const roomMatches = !studentRoomType || room.roomType === studentRoomType;
  const typeLabel = {
    '4sharing':'4 Sharing','3sharing':'3 Sharing','2sharing':'2 Sharing (Twin)',
    '2sharingPremium':'2 Sharing (Premium)','private':'Single / Private'
  };

  return (
    <div style={{
      background: 'white',
      border: '2px solid #E8E4DF',
      borderRadius:8, padding:10, minWidth:130,
      position:'relative',
    }}>
      <div style={{fontSize:12,fontWeight:800,color:'#1B3B5F'}}>{room.roomCode}</div>
      <div style={{fontSize:9,color:'#9B9B9B',marginBottom:4}}>{occ}/{total} occupied</div>

      {/* Room type mismatch overlay */}
      {!roomMatches && (
        <div style={{
          fontSize:8, fontWeight:700, color:'#E85D4A',
          background:'#FFF0EE', border:'1px solid #FFCDD2',
          borderRadius:4, padding:'3px 6px', marginBottom:6,
          textAlign:'center', letterSpacing:0.3,
        }}>
          {typeLabel[room.roomType] || room.roomType} — Not your room type
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:`repeat(${Math.min(total,4)},1fr)`,gap:4}}>
        {(room.beds||[]).map(bed=>{
          const isOcc     = bed.status==='OCCUPIED';
          const isSel     = selectedBed?.bedCode===bed.bedCode;
          const isBlocked = !roomMatches;
          return (
            <div key={bed.bedCode}
              onClick={()=>{ if(!isOcc && !isBlocked) onSelect(bed); }}
              title={
                isBlocked ? `Room type mismatch — student selected ${typeLabel[studentRoomType]||studentRoomType}`
                : isOcc   ? `Occupied by ${bed.studentName||'someone'}`
                : `Select ${bed.bedCode}`
              }
              style={{
                padding:'8px 2px',borderRadius:4,textAlign:'center',
                fontSize:9,fontWeight:700,
                cursor: isBlocked||isOcc ? 'not-allowed' : 'pointer',
                background: isSel ? '#4CAF50' : isOcc ? '#DC3545' : '#D3D3D3',
                color: (isOcc||isSel) ? 'white' : '#555',
                border:isSel?'2px solid #2E7D32':'2px solid transparent',
                transform:isSel?'translateY(-2px)':'none',
                transition:'all 0.15s',
              }}>
              {bed.bedCode}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Detail Drawer ──────────────────────────── */
const DetailDrawer = ({ adm, onClose, onPresence, onStatus }) => {
  if (!adm) return null;
  const Row = ({l,v}) => (
    <div style={{display:'flex',padding:'10px 0',borderBottom:'1px solid #F0EDE8',gap:12}}>
      <div style={{width:140,fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:'#6B6B6B',flexShrink:0,paddingTop:2}}>{l}</div>
      <div style={{fontSize:13,color:'#2C2C2C'}}>{v||'—'}</div>
    </div>
  );
  return (
    <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',justifyContent:'flex-end'}}>
      <div style={{flex:1,background:'rgba(0,0,0,0.3)'}} onClick={onClose}/>
      <div style={{width:400,background:'#fff',boxShadow:'-8px 0 40px rgba(0,0,0,0.15)',overflowY:'auto',padding:32}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24}}>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:0}}>Admission Details</h2>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#aaa'}}>✕</button>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:24,padding:14,background:'#F8F6F3',borderRadius:4}}>
          <Avatar name={adm.fullName} gender={adm.gender} studentId={adm.studentId} size={48}/>
          <div>
            <div style={{fontWeight:600,fontSize:15,color:'#1B3B5F'}}>{adm.fullName}</div>
            <div style={{fontSize:12,color:'#6B6B6B',marginTop:2}}>Student ID: {adm.admissionNumber}</div>
          </div>
        </div>
        <Row l="Student ID"   v={adm.admissionNumber}/>
        <Row l="Reg. Ref. ID" v={adm.registrationId}/>
        <Row l="Mobile"       v={adm.mobile}/>
        <Row l="Email"        v={adm.email}/>
        <Row l="Room"         v={adm.roomBlock&&adm.roomNumber?`${adm.roomBlock} – ${adm.roomNumber}`:null}/>
        <Row l="Bed"          v={adm.bedNumber}/>
        <Row l="Total Fees"   v={adm.totalFees?`₹ ${Number(adm.totalFees).toLocaleString()}`:null}/>
        {/* Presence control — kept as in your original */}
        <div style={{marginTop:20,padding:16,background:'#F8F6F3',borderRadius:4}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:'#6B6B6B',marginBottom:12}}>
            Presence
          </div>
          <InOutBadge presence={adm.presence} />
        </div>
      </div>
    </div>
  );
};

/* ─── Pagination ─────────────────────────────── */
const Pagination = ({ page, totalPages, size, total, onPage, onSize }) => {
  if (!totalPages) return null;
  const pages = Array.from({length:totalPages},(_,i)=>i);
  const Btn = ({label,onClick,disabled,active}) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding:'7px 11px',border:active?'none':'1px solid #E8E4DF',borderRadius:4,minWidth:32,
      background:active?'#1B3B5F':disabled?'#F8F6F3':'#fff',
      color:active?'#fff':disabled?'#ccc':'#2C2C2C',
      cursor:disabled?'default':'pointer',fontSize:13,fontWeight:active?700:400,
    }}>{label}</button>
  );
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px 40px',borderTop:'1px solid #E8E4DF',background:'#fff'}}>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <Btn label="«" onClick={()=>onPage(0)} disabled={page===0}/>
        <Btn label="‹" onClick={()=>onPage(page-1)} disabled={page===0}/>
        {pages.map(i=><Btn key={i} label={i+1} onClick={()=>onPage(i)} active={i===page}/>)}
        <Btn label="›" onClick={()=>onPage(page+1)} disabled={page>=totalPages-1}/>
        <Btn label="»" onClick={()=>onPage(totalPages-1)} disabled={page>=totalPages-1}/>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'#6B6B6B'}}>
        <span>No. of items per page</span>
        {[10,20,30,40,50,100].map(s=>(
          <button key={s} onClick={()=>onSize(s)} style={{
            padding:'5px 11px',border:'none',borderRadius:4,cursor:'pointer',fontSize:13,fontWeight:600,
            background:size===s?'#D4AF37':'#F0EDE8', color:size===s?'#fff':'#888',
          }}>{s}</button>
        ))}
      </div>
    </div>
  );
};

/* ─── MAIN SCREEN ────────────────────────────── */

const Toast = ({ message, type='success', onHide }) => {
  React.useEffect(() => { const t = setTimeout(onHide, 6000); return () => clearTimeout(t); }, [onHide]);
  const bg = type === 'success' ? '#2D5F4F' : '#1B3B5F';
  return (
    <div style={{ position:'fixed', top:24, right:24, zIndex:99999,
      background:bg, color:'#fff', padding:'14px 22px', borderRadius:8,
      boxShadow:'0 8px 32px rgba(0,0,0,0.22)', display:'flex', alignItems:'center',
      gap:12, fontSize:14, fontWeight:600, minWidth:300, animation:'toastIn 0.3s ease' }}>
      <span style={{ fontSize:18 }}>{type==='success' ? '✓' : 'ℹ️'}</span>
      <span>{message}</span>
      <button onClick={onHide} style={{ marginLeft:'auto', background:'none', border:'none', color:'#fff', fontSize:18, cursor:'pointer', opacity:0.7 }}>×</button>
      <style>{'@keyframes toastIn{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}'}</style>
    </div>
  );
};

const AdmissionScreen = ({ onNavigate, onLogout }) => {
  const [data,      setData]      = useState({content:[],totalPages:0,totalElements:0});
  const [stats,     setStats]     = useState({total:0,pending:0,allocated:0,approved:0,rejected:0,currentlyIn:0,currentlyOut:0});
  const [toast, setToast] = useState(null); // {message, type}
  const [page,      setPage]      = useState(0);
  const [size,      setSize]      = useState(10);
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [allocFor,  setAllocFor]  = useState(null);
  const [detail,    setDetail]    = useState(null);


const [sortField, setSortField] = useState('studentId');
const [sortOrder, setSortOrder] = useState('asc');

const sortedData = [...data.content].sort((a, b) => {

  if (!sortField) return 0;

  let valA, valB;

  if (sortField === "studentId") {
    valA = a.admissionNumber || '';
    valB = b.admissionNumber || '';
  }

  if (sortField === "name") {
    valA = a.fullName?.toLowerCase() || '';
    valB = b.fullName?.toLowerCase() || '';
  }

  if (sortField === "validDays") {
    valA = a.validTill ? new Date(a.validTill) : 0;
    valB = b.validTill ? new Date(b.validTill) : 0;
  }

  if (valA < valB) return sortOrder === "asc" ? -1 : 1;
  if (valA > valB) return sortOrder === "asc" ? 1 : -1;
  return 0;
});



  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [admData, statsData] = await Promise.all([
        admissionService.getAll(page, size, search),
        admissionService.getStats(),
      ]);
      setData(admData); setStats(statsData);
    } catch(e) { setError(e.message||'Failed to load admissions'); }
    finally { setLoading(false); }
  }, [page, size, search]);

  useEffect(() => { load(); }, [load]);

  const togglePresence = async (id, presence) => {
    try {
      await admissionService.updatePresence(id, presence);
      setData(d=>({...d,content:d.content.map(a=>a.id===id?{...a,presence}:a)}));
      if(detail?.id===id) setDetail(d=>({...d,presence}));
    } catch(e) { alert(e.message||'Failed'); }
  };

  // const TH = ({label,w}) => (
  //   <th style={{padding:'18px 14px',textAlign:'left',color:'#fff',fontSize:10,fontWeight:700,letterSpacing:1.5,textTransform:'uppercase',whiteSpace:'nowrap',width:w,borderRight:'1px solid rgba(255,255,255,0.08)'}}>
  //     {label}
  //   </th>
  // );
  // ✅ ADD THIS HERE
  const TH = ({label,w,field}) => (
    <th
      onClick={()=>{
        if(!field) return;

        setSortField(field);
        setSortOrder(sortOrder==="asc"?"desc":"asc");
      }}
      style={{
        padding:'18px 14px',
        textAlign:'left',
        color:'#fff',
        fontSize:10,
        fontWeight:700,
        letterSpacing:1.5,
        textTransform:'uppercase',
        whiteSpace:'nowrap',
        width:w,
        borderRight:'1px solid rgba(255,255,255,0.08)',
        cursor: field ? 'pointer' : 'default'
      }}
    >
      {label} {sortField===field && (sortOrder==="asc"?"▲":"▼")}
    </th>
  );
  const TD = ({children,style}) => (
    <td style={{padding:'14px',fontSize:13,verticalAlign:'middle',borderRight:'1px solid #E8E4DF',...style}}>{children}</td>
  );

  return (
    <div className="app-layout">
      {toast && <Toast message={toast.message} type={toast.type} onHide={() => setToast(null)} />}
      <Sidebar activeItem="admission" onNavigate={onNavigate} onLogout={onLogout}/>
      <main className="main-content" style={{padding:0,display:'flex',flexDirection:'column'}}>

        {/* Top bar */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'22px 40px',borderBottom:'1px solid #E8E4DF',background:'#fff',position:'sticky',top:0,zIndex:50}}>
          <h1 style={{fontFamily:'Cormorant Garamond,serif',fontSize:32,color:'#1B3B5F',fontWeight:400}}>Admissions</h1>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 20px',background:'#F8F6F3',borderRadius:30}}>
              <span style={{fontSize:15,color:'#888'}}>🔍</span>
              <input style={{border:'none',background:'transparent',outline:'none',fontSize:14,width:220,color:'#2C2C2C',fontFamily:'Work Sans,sans-serif'}}
                placeholder="Search students…" value={search} onChange={e=>{setSearch(e.target.value);setPage(0);}} />
            </div>
          </div>
        </div>

        {/* Stats bar — kept commented out as in original */}
        {/* <div>...</div> */}

        {error && <div style={{margin:'16px 40px',padding:'12px 16px',background:'#FFF3F3',borderLeft:'4px solid #E85D4A',color:'#E85D4A',fontSize:13,borderRadius:2}}>{error}</div>}

        {/* Table */}
        <div style={{flex:1,overflowX:'auto',background:'#fff'}}>
          {loading ? (
            <div style={{padding:60,textAlign:'center',color:'#6B6B6B',fontSize:15}}>Loading admissions…</div>
          ) : data.content.length===0 ? (
            <div style={{padding:70,textAlign:'center'}}>
              <div style={{fontSize:44,marginBottom:14}}>📋</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:24,color:'#1B3B5F',marginBottom:10}}>
                {search?'No results found':'No admissions yet'}
              </div>
              <p style={{color:'#6B6B6B',fontSize:14,marginBottom:24}}>
                {search?'Try a different search.':'Register students and confirm admissions to see them here.'}
              </p>
              {!search && <button onClick={()=>onNavigate('registration1')} style={{padding:'12px 28px',background:'#1B3B5F',color:'#fff',border:'none',borderRadius:4,cursor:'pointer',fontSize:13,fontWeight:600}}>+ Register a Student</button>}
            </div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',minWidth:1100}}>
              <thead>
                <tr style={{background:'#1B3B5F'}}>
                  <TH label="Student ID" w={100} field="studentId"/>
                  <TH label="" w={50}/>
                  <TH label="Student" w={200} field="name"/>
                  <TH label="P/A" w={60}/>
                  <TH label="Room No." w={130}/>
                  <TH label="Parents Name" w={170}/>
                  <TH label="Parents No." w={130}/>
                  <TH label="IN/OUT" w={80}/>
                  <TH label="Contact No" w={130}/>
                  <TH label="Email ID" w={200}/>
                  <TH label="Valid Days" w={100} field="validDays"/>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((adm,idx)=>(
                  <tr key={adm.id}
                    onClick={()=>onNavigate('studentAnalytics', { studentId: adm.studentId })}
                    style={{borderBottom:'1px solid #E8E4DF',background:idx%2===0?'#fff':'#FAFAFA',cursor:'pointer',transition:'background 0.15s'}}
                    onMouseEnter={e=>e.currentTarget.style.background='#F0F4F8'}
                    onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?'#fff':'#FAFAFA'}
                  >
                    <TD style={{fontFamily:'monospace',color:'#1B3B5F',fontWeight:600}}>{adm.admissionNumber}</TD>
                    <TD><Avatar name={adm.fullName} gender={adm.gender} studentId={adm.studentId}/></TD>
                    <TD style={{fontWeight:600,color:'#2C2C2C'}}>{adm.fullName}</TD>
                    <TD><PABadge presence={adm.presence}/></TD>
                    <TD>
                      {(adm.bedCode||adm.roomBlock)
                        ? <span style={{fontFamily:'monospace',fontWeight:600,color:'#1B3B5F'}}>
                            {adm.bedCode || `${adm.roomBlock} – ${adm.roomNumber}`}
                          </span>
                        : <span onClick={e=>{e.stopPropagation();setAllocFor(adm);}}
                            style={{color:'#E85D4A',fontWeight:600,cursor:'pointer',
                              textDecoration:'underline',fontSize:12}}>
                            Allocate
                          </span>
                      }
                    </TD>
                    <TD>{adm.fatherName || adm.motherName || '—'}</TD>
                    <TD style={{fontFamily:'monospace'}}>{adm.fatherMobile || adm.motherMobile || '—'}</TD>
                    <TD>
                      <span onClick={e => e.stopPropagation()}>
                        <InOutBadge presence={adm.presence}/>
                      </span>
                    </TD>
                    <TD style={{fontFamily:'monospace'}}>{adm.mobile||'—'}</TD>
                    <TD style={{color:'#4A7BA7',fontSize:12}}>{adm.email}</TD>
                    <TD>
                      {adm.validTill
                        ? <span style={{color:'#1B3B5F',fontWeight:600,textDecoration:'underline',cursor:'pointer'}}>
                            {Math.max(0,Math.ceil((new Date(adm.validTill)-new Date())/(1000*60*60*24)))}
                          </span>
                        : '—'}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && data.content.length>0 && (
          <Pagination page={page} totalPages={data.totalPages} size={size} total={data.totalElements} onPage={setPage} onSize={s=>{setSize(s);setPage(0);}}/>
        )}

        <div style={{background:'#333',color:'#fff',textAlign:'center',padding:'14px',fontSize:12}}>
          2026 Copyright © Omkara Hostel. All Rights Reserved.
        </div>
      </main>

      {/* ── Real 3-step bed picker (replaces old manual form) ── */}
      {allocFor && (
        <AllocateModal
          student={allocFor}
          onClose={()=>setAllocFor(null)}
          onSave={()=>{ setAllocFor(null); load(); setToast({ message: 'Bed allocated successfully! ✅', type:'success' }); }}
        />
      )}

      {detail && <DetailDrawer adm={detail} onClose={()=>setDetail(null)} onPresence={togglePresence} onStatus={()=>{}}/>}
    </div>
  );
};

export default AdmissionScreen;